import LazyHydrate from 'react-lazy-hydration'
import { ExternalLink } from '@/atoms/ExternalLink'
import { InternalLink } from '@/atoms/InternalLink'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { paths } from '@/constants/paths'
import { LegalLayout } from '@/layouts/LegalLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { getGenericStaticPaths } from '@/utils/nextUtils/staticPages'
import { loadTranslations } from '@/utils/translate/translate-server'

const orderedListRoman = 'list-[lower-roman] list-inside ml-4'
const orderedListAlpha = 'list-[lower-alpha] list-inside ml-4'

const MarketplaceTermsOfServicePage: NextPageWithLayout = () => {
  return (
    <div className="bg-white py-20 text-justify text-black">
      <LazyHydrate whenVisible>
        <PaddedContainer>
          <div className="flex flex-col items-center gap-4 p-10">
            <h1 className="text-center text-[1.3rem] font-bold md:text-[2rem]">
              <p>Angel Studios</p>
              <p>ANGEL MARKETPLACE TERMS OF SERVICE</p>
            </h1>
            <p>Updated: December 22, 2023</p>
            <p>
              Welcome to the Angel Marketplace (“AMP”), owned and operated by Angel Studios, Inc. (“Angel Studios” or
              “Angel”). We hope you’ll love our services! We may refer to you by the use of “you,” “your,” “artist,”
              “collector,” “owner,” or “user.” We may refer to ourselves as “we,” “us,” or “our.”
            </p>
            <p>
              AMP is a community-centered and community-led marketplace that provides users with the unique opportunity
              to purchase, collect, and showcase digital collectibles containing exclusive content from Angel. AMP
              provides a peer-to-peer web3 service that helps users discover and directly interact with each other and
              NFTs available on blockchains. These Terms of Service (“Terms”) govern your use of the content,
              functionality, and services offered on or through AMP on{' '}
              <InternalLink className="underline" href={paths.home}>
                {paths.getBaseURL()}
              </InternalLink>{' '}
              or{' '}
              <InternalLink className="underline" href={paths.collectiblesMarketplace.index}>
                {paths.getBaseURL()}
                {paths.collectiblesMarketplace.index}
              </InternalLink>{' '}
              (the “Sites”), including your pay-it-forward contribution(s) (if any), donation(s) (if any), tips (if
              any), and your purchase, sale, and trade of Angel non-fungible tokens (“Angel NFTs”) or any interaction
              with the AMP, which may include, now or in the future, the Angel Ethereum Sidechain (“Angel Sidechain”)
              (collectively, the “Services”).
            </p>
            <p>
              PLEASE NOTE, THESE TERMS DO NOT APPLY TO ANY OTHER SERVICES OR OTHER ACTIVITIES OPERATED OR OTHERWISE MADE
              AVAILABLE BY ANGEL STUDIOS OR ITS AFFILIATES, INCLUDING, WITHOUT LIMITATION, THE ANGEL STUDIOS STREAMING
              SERVICE, WHICH IS GOVERNED BY THE ANGEL STUDIOS TERMS OF USE, AVAILABLE AT{' '}
              <InternalLink className="underline" href={paths.legal.termsOfUse}>
                {paths.getBaseURL()}
                {paths.legal.termsOfUse}
              </InternalLink>
              , OR THE ANGEL FUNDING WEBSITE OPERATED BY OUR AFFILIATE, VAS PORTAL, LLC, WHICH IS GOVERNED BY THE ANGEL
              FUNDING TERMS OF SERVICE, AVAILABLE AT{' '}
              <ExternalLink className="underline" href={paths.external.invest.termsOfService}>
                {paths.external.invest.termsOfService}
              </ExternalLink>
              .
            </p>

            <ol className="list-inside list-decimal">
              <li>
                <span className="font-semibold">Your Acceptance of the Terms and Future Modifications</span>{' '}
                <p>
                  Angel Studios is pleased to provide you the Services upon your acceptance of these Terms. If you do
                  not agree to the Terms, do not use the Services.{' '}
                  <span className="font-semibold">
                    Please read these Terms carefully and completely as they set forth your rights and obligations with
                    respect to your use of our Services, including class action and jury trial waivers and the
                    acceptance of a binding arbitration agreement.
                  </span>{' '}
                  Your rights to use our Services are limited by applicable federal, state, and local laws and
                  regulations.
                </p>
                <p className="mt-4">
                  The Terms may be modified by Angel Studios from time to time, with or without providing notice
                  directly to you. We may do so without limitation by posting the revised Terms on our Sites and
                  publishing a general notice of such changes on our Sites or notifying you directly of such changes.
                  You can review the most current version of the Terms at any time by returning to this webpage. By
                  accessing or using our Services after we have provided such notice, you agree to be bound by such
                  modification, provided, however, the Terms in effect at the time your Angel NFT transaction is
                  completed by you will govern that purchase. You are encouraged to review these Terms prior to
                  completing a transaction on or through the Services or whenever you visit our Sites.
                </p>
                <p className="mt-4 font-semibold">
                  If you are dissatisfied with these Terms or any of the Services, your sole and exclusive remedy is to
                  discontinue using the Services or the applicable portion thereof.
                </p>
              </li>
              <li className="mt-4">
                <span className="font-semibold">Access and License to Services</span>
                <ol className={orderedListRoman}>
                  <li className="mt-4">
                    <span className="font-semibold">Eligibility.</span> You must be at least 13 years old to use the
                    Services, and you must be at least 18 years old to register an account on the Services. If you are
                    under 18, you must have your parent’s or legal guardian’s permission to use the Services. If this
                    applies to you, please read these Terms and our{' '}
                    <InternalLink className="underline" href={paths.legal.privacyPolicy}>
                      Privacy Policy
                    </InternalLink>{' '}
                    with your parent or legal guardian.{' '}
                    <span className="font-semibold">
                      If you are a parent or legal guardian and you give your child permission to use our Services,
                      these Terms apply to you, and you are responsible for your child’s use of our Services.
                    </span>
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">License.</span> Angel Studios hereby grants you a revocable and
                    nonexclusive right and license to use and access the Services in a manner that is consistent with
                    the Terms and the Services’ intended purposes. We reserve the right to terminate this license at our
                    sole discretion and without notice.
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Access.</span> Angel Studios is providing you with the information
                    and functionality in the Services. In accessing the Services, you are responsible for providing all
                    equipment necessary to establish a connection to the Internet, access to the Internet, and any
                    telephone, wireless or other connection and service fees associated with such access. Any and all
                    parts of the Services are subject to availability.
                    <p className="mt-4">
                      Angel Studios has the right to take any of the following actions in our sole discretion without
                      notice and without any liability for any reason:
                    </p>
                    <ol className={orderedListAlpha}>
                      <li className="mt-4">change or terminate all or any part of the Services;</li>
                      <li className="mt-4">restrict or terminate your access to all or any part of the Services; or</li>
                      <li className="mt-4">
                        refuse, move, or remove any content that is available on the Services and any material that you
                        submit to the Services.
                      </li>
                    </ol>
                  </li>
                </ol>
              </li>
              <li className="mt-4">
                <span className="font-semibold">Account and Password Security</span>
                <p className="mt-4">
                  You agree that any personal data you provide when you register an account with us or place an order is
                  accurate, current, and complete in all respects; and that you will notify us immediately of any
                  changes to the personal data by updating these details on your account. You agree not to impersonate
                  any other person or entity or to use a false name or a name that you are not authorized to use. You
                  acknowledge that your account is personal to you and are responsible for maintaining the
                  confidentiality of your password and account. You are fully responsible for all activities that occur
                  under your password and account.
                </p>
                <p className="mt-4">
                  Angel Studios cannot and will not be liable for any loss or damage arising from your failure to comply
                  with this provision or any of the Terms, including loss or damages of any kind that may arise as a
                  result of someone else using your password or account, either with or without your knowledge. However,
                  you may be held liable for any losses incurred by us or another party due to someone else using your
                  account or password. If we suspend or terminate any of your accounts under these Terms, you
                  acknowledge that all information and content associated with such account will no longer be available
                  to you.
                </p>
              </li>
              <li className="mt-4">
                <span className="font-semibold">User Conduct</span>
                <p className="mt-4">
                  By using the Services, you may need to interact with other users. You are solely responsible for any
                  such interaction and agree to do so in a manner that is legal, respectful, and consistent with these
                  Terms. Angel Studios is not responsible for the conduct of any other user who may interact with you,
                  regardless of whether or not the contact is made through the Services. Without limitation, you agree
                  not to engage in any activity on the Services that:
                </p>
                <ol className={orderedListRoman}>
                  <li className="mt-4">
                    impersonates any person or entity, misrepresents your connection or affiliation with a person or
                    entity, or otherwise communicates false or misleading information or content through the Platform;
                  </li>
                  <li className="mt-4">
                    defames, threatens, abuses, or otherwise violates the legal rights of others;
                  </li>
                  <li className="mt-4">
                    is harmful to children, profane, obscene, indecent, or racially or ethnically offensive;
                  </li>
                  <li className="mt-4">
                    infringes another’s rights to intellectual property, publicity, privacy, or contract;
                  </li>
                  <li className="mt-4">collects or stores personal information about other users of the Services;</li>
                  <li className="mt-4">
                    contains, transmits, or disseminates viruses, corrupted files, or any other similar software or
                    programs that may damage or adversely affect the operation of another’s computer, the Services, or
                    any software, hardware, or other related equipment;
                  </li>
                  <li className="mt-4">
                    attempts to defeat or circumvent security features, or attempts to access or utilize the Services
                    for any purpose other than its intended purposes;
                  </li>
                  <li className="mt-4">
                    disrupts, imposes an unreasonable or disproportionately large strain on, or otherwise interferes
                    with the Services or the networks or servers used by Angel Studios;
                  </li>
                  <li className="mt-4">
                    engage in any activity that contains unsolicited or unauthorized advertisements, promotions,
                    commercial solicitations, contests, or surveys;
                  </li>
                  <li className="mt-4">
                    use any automated technology such as a robot, spider, or scraper to access, scrape, or data mine the
                    Platform;
                  </li>
                  <li className="mt-4">violates any federal, state, or local laws or regulations;</li>
                  <li className="mt-4">constitutes, discusses, incites, or promotes illegal activity; or</li>
                  <li className="mt-4">otherwise violates these Terms, as determined by us.</li>
                </ol>
                <p className="mt-4">
                  Angel Studios, in its sole discretion, will determine what constitutes abuse or misuse of the Services
                  and these Terms. We reserve the right to terminate your access to the Services or any portion thereof
                  at any time without notice for any reason whatsoever.
                </p>
              </li>

              <li className="mt-4">
                <span className="font-semibold">Terms of Sale of Angel NFTs and Transactions through AMP</span>
                <ol className={orderedListRoman}>
                  <li className="mt-4">
                    <span className="font-semibold">Transactions through Our Services.</span> Our Services allows you to
                    purchase, earn, collect, and showcase Angel NFTs. When each Angel NFT is sold for the first time,
                    the agreement for sale is between Angel and the initial purchaser. By placing an order on our
                    Services, you agree that you are submitting a binding offer to purchase an Angel NFT or other
                    product. All transactions will be conducted in English. If you are the initial purchaser of an Angel
                    NFT, then all amounts due are to be paid to Angel Studios, less a royalty to the creator of the
                    content of the Angel NFT. If you are not the initial purchaser of an Angel NFT, then amounts may be
                    paid to the then-holder of the Angel NFT, less a transaction fee to Angel and royalty to the creator
                    of the content of the Angel NFT. If the initial purchaser decides to sell the Angel NFT through our
                    Services, then our Services only serve as a platform that facilitates transactions between a buyer
                    and a seller and we are not a party to any agreement between such buyer and seller of Angel NFTs (or
                    any other NFTs) or between any other users.
                    <p className="mt-4">
                      We do not store, send, or receive NFTs. Each Angel NFT is recorded on the Angel Sidechain or other
                      applicable blockchain network, as determined by us in our sole discretion (“Angel Network”). The
                      Angel Network will use a computation protocol-verifiable ledger to prevent double trades of NFTs
                      and use self-certifying identifiers, strongly bound to a cryptographic signing (public, private)
                      key-pair, when they are minted or transferred.{' '}
                    </p>
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Your Ownership of an Angel NFT.</span> When you buy an Angel NFT,
                    you own the underlying NFT and have the right to sell or give away the Angel NFT. You understand and
                    agree that the sale of Angel NFTs grants you no rights and carries with it no guarantee of future
                    performance of any kind by Angel Studios. You are not entitled, as a holder of any Angel NFT, to
                    vote or receive dividends or profits or be deemed the holder of shares of Angel Studios or any other
                    person by virtue of your ownership of an Angel NFT, nor will anything contained herein be construed
                    to grant you any of the rights of a shareholder, partner, joint venturer, etc. or any right to vote
                    for the election of directors or upon any matter submitted to shareholders at any meeting thereof,
                    or to give or withhold consent to any corporate action or to receive notice of meetings, or to
                    receive subscription rights to purchase such shares of Angel Studios or any related entity.
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Linking Your Cryptocurrency Wallet.</span> We are not a wallet
                    provider, exchange, broker, financial institution, or creditor. To use our Services, you must use a
                    third-party wallet, which allows you to engage in transactions on blockchains and enables you to
                    purchase, store, and engage in transactions using your credit card, Angel tokens (“Lumens”), or via
                    one or more fiat-cash or cryptocurrencies that we may elect to accept from time to time. Your
                    account on our Services will be associated with your linked blockchain address and wallet and will
                    display the NFTs for that blockchain address (and, if applicable, any content associated with such
                    NFTs). By using your wallet in connection with the Services, you agree that you are using that
                    wallet under the terms and conditions of the applicable provider of the wallet. Wallets are not
                    operated by, maintained by, or affiliated with Angel Studios, and Angel Studios does not have
                    custody or control over the contents of your wallet and have no ability to retrieve or transfer its
                    contents. Angel Studios accepts no responsibility for, or liability to you, in connection with your
                    use of a wallet and makes no representations or warranties regarding how the Services will operate
                    with any specific wallet.
                    <p className="mt-4">
                      When you link your wallet, you understand and agree that you are solely responsible for
                      maintaining the security of your wallet and your control over any wallet-related authentication
                      credentials, private or public cryptocurrency keys, NFTs or cryptocurrencies that are stored in or
                      are accessible through your wallet. Any unauthorized access to your wallet by third parties could
                      result in the loss or theft of Angel NFTs or funds held in your wallet and any associated wallets,
                      including any linked financial information such as bank accounts or credit cards. We are not
                      responsible for managing and maintaining the security of your wallet nor for any unauthorized
                      access to or use of your wallet. If you notice any unauthorized or suspicious activity in your
                      wallet that seems to be related to our Services or Angel NFTs, please notify us immediately. Angel
                      Studios reserves the right to place your account on hold at any time, with or without notifying
                      you, to protect itself and its partners from what it believes to be fraudulent activity.
                    </p>
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Royalties and Fees.</span> You acknowledge and agree that the
                    content creator receives 5% of every subsequent sale of an Angel NFT (“Royalty”). The Royalty is
                    paid evenly by the seller and the purchaser. For example, if you are the initial purchaser and you
                    sell an Angel NFT for $100 to a subsequent purchaser, the subsequent purchaser will pay an
                    additional $2.50 above the listed sales price Angel NFT and $2.50 of the amount to be received by
                    the you (the initial purchaser) will be automatically transferred to the content creator. Content
                    creators have the right to collect Royalties for all Angel NFT sales in perpetuity.
                    <p className="mt-4 font-semibold">
                      As such, if you sell an Angel NFT on a third-party NFT marketplace, you agree to include a
                      statement substantially similar to the following in the description of the NFT:
                    </p>
                    <p className="mt-4">“5% Royalty Applies. See Angel Marketplace Terms for details.”</p>
                    <p className="mt-4">
                      In addition, when you buy or sell an Angel NFT on AMP, you agree to pay a 2.5% transaction fee,
                      and you authorize us to automatically charge and collect such fees from your payment. We will
                      always display a breakdown of any transaction or other fees prior to your purchase or sale of an
                      NFT. NFT transaction fees do not include sales taxes where applicable. Please see Section 12
                      Taxes.
                    </p>
                    <p className="mt-4">
                      At any time, and for any reason, we may provide a discount, Lumens, or other consideration to some
                      or all of our members (“Credits”). The amount and form of any Credits, and the decision to provide
                      them, are at our sole and absolute discretion. The provision of a Credit in one instance will not
                      entitle you to a Credit in a similar instance, nor will it obligate us to provide any other
                      Credits in the future, under any circumstance. From time to time, promotional codes may be
                      available, including those provided as part of a third-party promotion. Promotional codes may be
                      redeemed as described in the specifics of the promotion. Promotional codes may be used only once,
                      cannot be redeemed for cash, and may be combined with other offers, subject to certain
                      restrictions as determined by Angel Studios in its sole discretion. If you received a promotional
                      code through an offer by a third party, additional conditions may apply. Users may also be
                      rewarded with a Lumen or an Angel NFT frame (“Angel NFT Frame”) of a particular short video clip
                      (“Moment”), as applicable and as decided by Angel in its sole discretion. Lumens and Angel NFT
                      Frames may be earned for free by participating in certain challenges or marketing campaigns
                      through Angel or our Services, or by completing certain tasks that we may make generally available
                      from time to time. Lumens are transferred to the wallet associated with a user’s account and may
                      be redeemed for Angel NFTs, fiat-cash, or cryptocurrency. Users who are gifted an Angel NFT Frame
                      own the Angel NFT Frame but not the surrounding Moment, surrounding scene, or the surrounding
                      video metadata. Each Angel NFT Frame is identified by a unique serial number and timestamp.
                    </p>
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">All Sales Final.</span> All sales, purchases, donations, tips,
                    pay-it-forward contributions, or deliveries through any of Angel NFTs, Lumens, or Angel NFT Frames
                    through our Services are final, non-cancellable, and non-refundable except with respect to any
                    statutory warranties or guaranties that cannot be excluded or limited by law.
                  </li>
                </ol>
              </li>
              <li className="mt-4">
                <span className="font-semibold">Refunds. </span>
                Thank you for shopping at Angel Studios. We strive to provide you with exceptional products and
                services. As part of our commitment to transparency and fairness, please review our refund policy
                outlined below:
                <ol className={orderedListRoman}>
                  <li className="mt-4">
                    <span className="font-semibold">No Refunds.</span> We do not offer refunds on purchases unless the
                    product(s) received are defective or damaged upon arrival. In such cases, please contact our
                    customer service team within 15 days of receiving the product(s) to initiate the return process.
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Defective or Damaged Products.</span> If you receive a defective or
                    damaged item, please contact us immediately with photographic evidence of the issue. Our customer
                    service team will assess the situation and provide instructions for returning the product(s) for an
                    exchange or store credit.
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Exceptions</span> Certain products or circumstances may warrant
                    exceptions to this policy. We reserve the right to evaluate and make decisions on a case-by-case
                    basis. Items must be in resalable condition.
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Non-Returnable Items</span> The following items are categorically
                    non-returnable: PIF contributions; movie tickets; Guild membership fees; collectible scenes and
                    frames; and other digital assets or goods.
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Return Shipping</span> Customers are responsible for return shipping
                    costs unless otherwise specified. Process Time: Please allow at least 14 days for us to process your
                    return and issue an exchange or store credit upon receipt of the returned item(s).
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Cancellation Policy</span> Orders cannot be canceled once they have
                    been processed and shipped. Please review your order carefully before completing the purchase.
                  </li>
                </ol>
                <p className="mt-4">
                  By making a purchase with Angel Studios, you acknowledge and agree to the terms of this refund policy.
                </p>
              </li>
              <li className="mt-4">
                <span className="font-semibold">Risks</span>
                <ol className={orderedListRoman}>
                  <li className="mt-4">
                    <span className="font-semibold">Characteristics and Subjectivity of NFTs.</span> Angel NFTs may be
                    comprised of a Moment or other digital material associated with Angel and its content. In general,
                    each Angel NFT has a defined set of attributes – including scarcity – which help determine the value
                    of the Angel NFT. The value of each Angel NFT is inherently subjective, in the same way the value of
                    other collectibles is inherently subjective. Each Angel NFT has no inherent or intrinsic value. Some
                    collectors might prefer to have an Angel NFT featuring a certain Moment, while another might prefer
                    an equivalent Angel NFT featuring a different Moment. Each Moment can have more than one Angel NFT
                    associated with them, and those Angel NFTs will each have different characteristics. To maintain
                    scarcity and exclusivity, Angel will release a certain number of Angel NFTs associated with a
                    particular Moment or Underlying Digital Art.
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Assumption of Risk.</span> You accept and acknowledge:
                    <ol className={orderedListAlpha}>
                      <li className="mt-4">
                        The value of an NFT, including an Angel NFT, is subjective. Prices of NFTs are subject to
                        volatility and fluctuations in the price of cryptocurrency can also materially and adversely
                        affect NFT prices.
                      </li>
                      <li className="mt-4">
                        The value of NFTs may be derived from the continued willingness of market participants to
                        exchange fiat currency or digital assets for NFTs, which may result in the potential for
                        permanent and total loss of value of a particular NFT should the market for that NFT disappear.
                      </li>
                      <li className="mt-4">
                        The regulatory regime governing blockchain technologies, NFTs, cryptocurrency, and other
                        crypto-assets is uncertain, and new regulations or policies may materially adversely affect the
                        development of the Services and the potential utility or value of NFTs.
                      </li>
                      <li className="mt-4">Angel NFTs are not legal tender and are not backed by any government.</li>
                      <li className="mt-4">
                        There are risks associated with purchasing items associated with content created by third
                        parties through peer-to-peer transactions, including but not limited to, the risk of purchasing
                        counterfeit items, mislabeled items, items that are vulnerable to metadata decay, items on smart
                        contracts with bugs, and items that may become untransferable. You represent and warrant that
                        you have done sufficient research before making any decisions to sell, obtain, transfer, or
                        otherwise interact with any NFTs.
                      </li>
                      <li className="mt-4">
                        We do not control the public blockchains that you are interacting with, and we do not control
                        certain smart contracts and protocols that may be integral to your ability to complete
                        transactions on these public blockchains. Additionally, blockchain transactions are irreversible
                        and we have no ability to reverse any transactions on the blockchain.
                      </li>
                      <li className="mt-4">
                        Some transactions in NFTs shall be deemed to be made when recorded on a public ledger, which is
                        not necessarily the date or time that you initiated the transaction.
                      </li>
                      <li className="mt-4">
                        There are risks associated with using Internet and blockchain based products, including, but not
                        limited to, the risk associated with hardware, software, and Internet connections, the risk of
                        malicious software introduction, and the risk that third parties may obtain unauthorized access
                        to your wallet or account. You accept and acknowledge that Angel Studios will not be responsible
                        for any communication failures, disruptions, errors, distortions or delays you may experience
                        when using the Service or any blockchain network, however caused.
                      </li>
                    </ol>
                  </li>
                </ol>
              </li>

              <li className="mt-4">
                <span className="font-semibold">Intellectual Property</span>
                <ol className={orderedListRoman}>
                  <li className="mt-4">
                    <span className="font-semibold">Ownership of Content of the Services.</span> Except as expressly set
                    forth herein or otherwise indicated in writing by us, your use of our Services does not grant you
                    ownership of, or any other rights with respect to any content, code, data, or other Angel content or
                    materials that you may access on or through our Services. We reserve all rights in and to materials
                    available or accessible through our Services that are not expressly granted to you in these Terms.
                    <p className="mt-4">
                      Through our Services, Angel Studios provides access to content. The copyright to that content,
                      including but not limited to, the art, design, and drawings (in any form or media, including,
                      without limitation, video or photographs) that may be associated with an Angel NFT that you own
                      (“Underlying Digital Art”), or performances are, in general, owned by third parties who have
                      produced or created it, or otherwise acquired copyrights associated with it. Without third-party
                      content creators, there would be little content for Angel Studios users to enjoy. Angel Studios is
                      committed to protecting the rights of all third-party copyright holders whose works it makes
                      available for your purchase and enjoyment. Pursuant to 17 U.S.C. Section 512, as amended by Title
                      II of the Digital Millennium Copyright Act (the “DMCA”), Angel Studios reserves the right to
                      terminate your right to use the Services if Angel Studios determines in its sole and absolute
                      discretion that you are involved in any activity that may be infringing, including alleged acts of
                      infringement, regardless of whether the material or activity is ultimately determined to be
                      infringing.
                    </p>
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Notice of Infringement.</span> If you believe that your work has
                    been copied in a way that constitutes copyright infringement, please send a detailed message to the
                    designated agent listed below with the following information (under the DMCA, the following
                    information must be included in the message):
                    <ol className={orderedListAlpha}>
                      <li className="mt-4">
                        an electronic or physical signature of the person authorized to act on behalf of the owner of
                        the copyright or other intellectual property interest;
                      </li>
                      <li className="mt-4">
                        an identification of the copyrighted work or other intellectual property that you claim has been
                        infringed, or, if multiple works at a single online site are covered by a single notification, a
                        representative list of such works at that site;
                      </li>
                      <li className="mt-4">
                        a description of where the material that you claim is infringing is located on in the Services
                        that is reasonably sufficient to permit Angel Studios to locate the material;
                      </li>
                      <li className="mt-4">
                        information reasonably sufficient to permit Angel Studios to contact you as the complaining
                        party, such as your address, telephone number, and email address;
                      </li>
                      <li className="mt-4">
                        a statement by you that you have a good faith belief that the disputed use is not authorized by
                        the copyright owner, its agent, or the law; and
                      </li>
                      <li className="mt-4">
                        a statement by you, made under penalty of perjury, that the above information in your notice is
                        accurate and that you are the copyright or intellectual property owner or authorized to act on
                        the copyright or intellectual property owner’s behalf.
                      </li>
                    </ol>
                    <p className="mt-4">
                      We may disclose DMCA notices to affected users and third party databases that collect information
                      about copyright takedown notices.
                    </p>
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Counter Notifications.</span> If your content is removed pursuant to
                    a notice of copyright infringement and you want to challenge the removal, you must provide us a
                    counter notification to the address provided below. Such counter-notification must contain the
                    following:
                    <ol className={orderedListAlpha}>
                      <li className="mt-4">
                        information reasonably sufficient to permit us to contact you, such as an address, telephone
                        number, and, if available, an electronic mail address;
                      </li>
                      <li className="mt-4">
                        a description of the material that was removed and where the material previously appeared on our
                        Platform reasonably sufficient to permit us to identify the material;
                      </li>
                      <li className="mt-4">
                        a statement, under penalty of perjury, that you have a good faith belief that the material was
                        removed or disabled as a result of mistake or misidentification;
                      </li>
                      <li className="mt-4">
                        a statement that you consent to the jurisdiction of the Federal District Court for the judicial
                        district in which your address is located, or, if your address is outside the United States, any
                        judicial district in which Angel Studios may be found (the United States District Court for the
                        District of Utah) and that you will accept service of process from the person who provided the
                        original DMCA notice or an agent of that person; and
                      </li>
                      <li className="mt-4">your physical or electronic signature.</li>
                    </ol>
                    <p className="mt-4">
                      We will forward any complete counter-notification to the person who provided the initial DMCA
                      notice. The copyright owner(s) may elect to file a lawsuit against you for copyright infringement.
                      If we do not receive notice that such a lawsuit has been filed within ten (10) business days after
                      we provide notice of your counter-notification, we may, but are not obligated to, restore your
                      content and other materials. Until that time, your content and other materials will remain
                      removed.
                    </p>
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Repeat Infringers.</span> Without limiting anything else in these
                    Terms, we will terminate your account if you receive three (3) DMCA Takedown Actions. A “DMCA
                    Takedown Action” occurs each time your content or other materials are removed due to a DMCA notice.
                    We may group multiple DMCA notices received in a short period of time as a single DMCA Takedown
                    Action. We may remove a DMCA Takedown Action in appropriate circumstances, such as where: (1) the
                    material is restored due to a DMCA counter-notification; or (2) the party who provided the DMCA
                    notice withdraws their complaint.
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Address for Notices.</span> DMCA notices and counter-notifications
                    may be sent to our designated agent, JUDD FUNK at the following addresses:
                    <p className="mt-4">
                      By email to:{' '}
                      <a className="underline" href="mailto:Judd.Funk@Angel.com">
                        Judd.Funk@Angel.com
                      </a>
                    </p>
                    <p className="mt-4">By mail to:</p>
                    <p className="mt-4">
                      <p>Angel Studios Inc.</p>
                      <p>Attn: Judd Funk</p>
                      <p>295 W Center St Suite A</p>
                      <p>Provo, UT 84601</p>
                    </p>
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Other Complaints and Questions.</span> If you believe that any
                    content in the Services violates or infringes your intellectual property rights, other than related
                    to copyright, please send a detailed email to{' '}
                    <a className="underline" href="mailto:legal@angel.com">
                      legal@angel.com
                    </a>{' '}
                    detailing your allegation.
                    <p className="mt-4">
                      If you are unsure whether a contemplated use of the Services or an Angel NFT and its content would
                      violate these Terms, please contact us at{' '}
                      <a className="underline" href="mailto:legal@angel.com">
                        legal@angel.com
                      </a>
                      .
                    </p>
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Personal Use.</span> Subject to your continued compliance with these
                    Terms, Angel Studios grants you a worldwide, royalty-free license to use, copy, and display
                    Underlying Digital Art, solely for the following purposes: (i) for your own personal, non-commercial
                    use; (ii) as part of a marketplace that permits the purchase and sale of your Angel NFT, provided
                    that the marketplace cryptographically verifies each NFT owner’s rights; or (iii) as part of a third
                    party website or application that permits the inclusion, involvement, or participation of your Angel
                    NFT, provided that the website/application cryptographically verifies each NFT owner’s rights, and
                    provided that the Underlying Digital Art is no longer visible once the owner of the Angel NFT leaves
                    the website/application.
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Restrictions on Usage.</span> You agree that you may not, nor may
                    permit any third party to do or attempt to do any of the following without our (or, as applicable,
                    our licensors’) express prior written consent in each case:
                    <ol className={orderedListAlpha}>
                      <li className="mt-4">
                        Modify or create derivative works of the Underlying Digital Art or Angel NFT in any way,
                        including, without limitation, the shapes, designs, drawings, attributes, or color schemes;
                      </li>
                      <li className="mt-4">
                        Use the Underlying Digital Art to advertise, market or sell third-party products;
                      </li>
                      <li className="mt-4">
                        Use the Underlying Digital Art or Angel NFT in connection with images, videos, or other forms of
                        media that depict hatred, intolerance, violence, cruelty, or anything else that could reasonably
                        be found to constitute hate speech or otherwise infringe upon the rights of other;
                      </li>
                      <li className="mt-4">
                        Use the Underlying Digital Art for your Angel NFT in movies, videos, or any other forms of
                        media, except to the limited extent that such use is expressly permitted in these Terms or
                        solely for your own personal, non-commercial use;
                      </li>
                      <li className="mt-4">
                        Sell, distribute for commercial gain (including, without limitation, giving away in the hopes of
                        eventual commercial gain), or otherwise commercialize merchandise that includes, contains, or
                        consists of the Underlying Digital Art for your Angel NFT;
                      </li>
                      <li className="mt-4">
                        Attempt to trademark, copyright, or otherwise acquire additional intellectual property rights in
                        or to the Underlying Digital Art or otherwise utilize the Underlying Digital Art for your Angel
                        NFT for you or any third party;
                      </li>
                      <li className="mt-4">
                        Submit any art or NFT content that (a) violates or encourages any conduct that would violate any
                        applicable law or regulation or would give rise to civil or criminal liabilities; (b) is
                        fraudulent, false, misleading or deceptive; (c) is defamatory, obscene, vulgar, pornography or
                        offensive; (d) promotes discrimination, bigotry, racism, hatred, harassment or harm against any
                        individual or group; (e) is violent or threatening or promotes violence or actions that are
                        threatening to any person or entity; or (f) promotes illegal or harmful activities or
                        substantives;
                      </li>
                      <li className="mt-4">
                        Attack, hack, DDOS, interfere with, or otherwise tamper with the Angel NFT or its underlying
                        smart contract;
                      </li>
                      <li className="mt-4">
                        Use the NFT or the Services provided to you via AMP or the App to conceal or transfer any
                        proceeds relating to illegal or criminal activity;
                      </li>
                      <li className="mt-4">Violate these Terms or any applicable law or regulation; or</li>
                      <li className="mt-4">Encourage or enable any third party to do any of the foregoing.</li>
                    </ol>
                  </li>
                </ol>
              </li>

              <li className="mt-4">
                <span className="font-semibold">Privacy</span> By accessing or using our Services, you acknowledge that
                your personal data may be collected, used, disclosed, transferred or shared as described in our Privacy
                Policy, available at{' '}
                <InternalLink className="underline" href={paths.legal.privacyPolicy}>
                  {paths.getBaseURL()}
                  {paths.legal.privacyPolicy}
                </InternalLink>
                . Our Privacy Policy may be updated from time to time, so please review it regularly. If you do not
                agree with the collection, use, disclosure, transfer, or sharing of your personal data as described in
                our Privacy Policy, please discontinue all access to and use of our Services.
              </li>

              <li className="mt-4">
                <span className="font-semibold">Disclaimers</span>
                <ol className={orderedListRoman}>
                  <li className="mt-4">
                    <span className="font-semibold">No Guarantees.</span> NFTS ARE INTANGIBLE DIGITAL ASSETS. THEY EXIST
                    ONLY BY VIRTUE OF THE OWNERSHIP RECORD MAINTAINED IN THE APPLICABLE BLOCKCHAIN NETWORK. ANY TRANSFER
                    OF TITLE THAT MIGHT OCCUR IN ANY UNIQUE DIGITAL ASSET OCCURS ON THE DECENTRALIZED LEDGER WITHIN SUCH
                    BLOCKCHAIN NETWORK, WHICH WE DO NOT CONTROL. WE DO NOT GUARANTEE THAT WE CAN EFFECT THE TRANSFER OF
                    TITLE OR RIGHT IN ANY NFT.
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Disclaimer of all Warranties.</span> TO THE FULLEST EXTENT PERMITTED
                    BY LAW, THE SERVICES AND CONTENT CONTAINED THEREIN, AND ANY AND ALL NFTS MINTED ARE PROVIDED ON AN
                    “AS IS” AND “AS AVAILABLE” BASIS WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND. WITHOUT LIMITING THE
                    FOREGOING, WE EXPLICITLY DISCLAIM ANY IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
                    PARTICULAR PURPOSE, QUIET ENJOYMENT AND NON-INFRINGEMENT, AND ANY WARRANTIES ARISING OUT OF COURSE
                    OF DEALING OR USAGE OF TRADE. WE MAKE NO WARRANTY THAT THE SERVICES WILL MEET YOUR REQUIREMENTS OR
                    BE AVAILABLE ON AN UNINTERRUPTED, SECURE, OR ERROR- FREE BASIS. WE MAKE NO WARRANTY REGARDING THE
                    QUALITY, ACCURACY, TIMELINESS, TRUTHFULNESS, COMPLETENESS OR RELIABILITY OF ANY INFORMATION OR
                    CONTENT ON THE SERVICES OR ANY NFT OR ITS UNDERLYING SMART CONTRACT OR BLOCKCHAIN NETWORK.
                    <p className="mt-4">
                      WE WILL NOT BE RESPONSIBLE OR LIABLE TO YOU FOR ANY LOSS AND TAKE NO RESPONSIBILITY FOR, AND WILL
                      NOT BE LIABLE TO YOU FOR, ANY USE OF THE SERVICES OR NFTS, INCLUDING BUT NOT LIMITED TO ANY
                      LOSSES, DAMAGES OR CLAIMS ARISING FROM: (1) USER ERROR SUCH AS FORGOTTEN PASSWORDS, INCORRECTLY
                      CONSTRUCTED TRANSACTIONS, OR MISTYPED WALLET ADDRESSES; (2) SERVER FAILURE OR DATA LOSS; (3)
                      CORRUPTED CRYPTOCURRENCY WALLET FILES; (4) UNAUTHORIZED ACCESS; OR (5) ANY UNAUTHORIZED THIRD
                      PARTY ACTIVITIES, INCLUDING WITHOUT LIMITATION THE USE OF VIRUSES, PHISHING, BRUTE FORCING OR
                      OTHER MEANS OF ATTACK AGAINST BLOCKCHAIN NETWORK UNDERLYING THE NFTS.
                    </p>
                    <p className="mt-4">
                      YOU HEREBY ACKNOWLEDGE THAT OUR SERVICES MAY NOT BE AVAILABLE DUE TO ANY NUMBER OF FACTORS
                      INCLUDING, BUT NOT LIMITED TO, PERIODIC SYSTEM MAINTENANCE, SCHEDULED OR UNSCHEDULED, ACTS OF GOD,
                      UNAUTHORIZED ACCESS, VIRUSES, DENIAL OF SERVICE OR OTHER ATTACKS, TECHNICAL FAILURE OF OUR
                      SERVICES AND/OR TELECOMMUNICATIONS INFRASTRUCTURE, OR DISRUPTION, AND, THEREFORE, WE EXPRESSLY
                      DISCLAIM ANY EXPRESS OR IMPLIED WARRANTY REGARDING THE USE AND/OR AVAILABILITY, ACCESSIBILITY,
                      SECURITY, OR PERFORMANCE OF OUR SERVICES CAUSED BY SUCH FACTORS.
                    </p>
                    <p className="mt-4">
                      WE ARE NOT RESPONSIBLE OR LIABLE FOR, NOR DO WE REPRESENT OR OTHERWISE WARRANT THE PERFORMANCE OF
                      ANY DEVICE YOU USE TO ACCESS OR USE OUR SERVICES, INCLUDING, WITHOUT LIMITATION, THE CONTINUING
                      COMPATIBILITY OF ANY DEVICE WITH OUR SERVICES. BY USING ANY DEVICE TO ACCESS OR USE OUR SERVICES
                      YOU AGREE TO LOOK SOLELY TO THE ENTITY THAT MANUFACTURED AND/OR SOLD YOU THE DEVICE FOR ANY ISSUES
                      RELATED TO THE DEVICE AND YOUR ABILITY OR INABILITY TO USE THE DEVICE TO ACCESS AND USE OUR
                      SERVICES.
                    </p>
                    <p className="mt-4">
                      SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF CERTAIN WARRANTIES. ACCORDINGLY, SOME OF THE
                      ABOVE DISCLAIMERS OF WARRANTIES MAY NOT APPLY TO YOU.
                    </p>
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Use of Services at Your Sole Risk.</span> YOUR ACCESS TO AND USE OF
                    OUR SERVICES, DOWNLOAD OF ANY SOFTWARE RELATING TO OUR SERVICES, AND USE OF ANY INFORMATION WE MAY
                    PROVIDE IN CONNECTION WITH YOUR ACCESS TO OR USE OF OUR SERVICES IS AT YOUR SOLE OPTION, DISCRETION,
                    AND RISK. WE SHALL NOT BE LIABLE FOR ANY MALFUNCTIONS OF THE SOFTWARE RELATING TO OUR SERVICES, BUGS
                    OR VIRUSES RESULTING IN LOST DATA OR ANY OTHER DAMAGE TO YOUR COMPUTER EQUIPMENT, MOBILE PHONE OR
                    MOBILE DEVICE, OR SOFTWARE. FURTHERMORE, WE SHALL NOT BE LIABLE FOR ANY ATTEMPTS BY YOU TO ACCESS OR
                    USE OUR SERVICES BY METHODS, MEANS OR WAYS NOT INTENDED BY US. WE ARE NOT REQUIRED TO PROVIDE
                    REDUNDANT OR BACKUP NETWORKS AND/OR SYSTEMS.
                    <p className="mt-4">
                      NO ADVICE OR INFORMATION, WHETHER ORAL OR WRITTEN OBTAINED BY YOU FROM ANY OF THE INDEMNIFIED
                      PARTIES THROUGH OR FROM OUR SERVICES SHALL CREATE ANY WARRANTY NOT EXPRESSLY STATED IN THESE
                      TERMS.
                    </p>
                    <p className="mt-4">
                      WE DO NOT ENDORSE AND ARE NOT RESPONSIBLE FOR THE STATEMENTS, ADVICE OR OPINIONS MADE BY ANYONE
                      OTHER THAN AUTHORIZED ANGEL STUDIOS SPOKESPERSONS. WE DO NOT ENDORSE AND ARE NOT RESPONSIBLE FOR
                      ANY STATEMENTS, ADVICE OR OPINIONS PROVIDED BY ANY THIRD PARTIES, AND SUCH STATEMENTS, ADVICE OR
                      OPINIONS DO NOT IN ANY WAY REFLECT THE STATEMENTS, ADVICE OR OPINIONS OF ANGEL STUDIOS.
                    </p>
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Limitations of our Liability.</span> WE ARE NOT RESPONSIBLE FOR ANY
                    KIND OF FAILURE, ABNORMAL BEHAVIOR OF SOFTWARE (E.G., WALLET, SMART CONTRACT), BLOCKCHAINS OR ANY
                    OTHER FEATURES OF THE NFTS.
                    <p className="mt-4">
                      TO THE EXTENT PERMISSIBLE UNDER APPLICABLE LAW, IN NO EVENT SHALL WE BE LIABLE TO YOU OR ANY THIRD
                      PARTY FOR CONSEQUENTIAL, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, PUNITIVE OR ENHANCED DAMAGES,
                      LOST PROFITS OR REVENUES OR DIMINUTION IN VALUE, ARISING OUT OF, OR RELATING TO, AND/OR IN
                      CONNECTION WITH ANY BREACH OF THESE TERMS, REGARDLESS OF (A) WHETHER SUCH DAMAGES WERE
                      FORESEEABLE, (B) WHETHER OR NOT WE WERE ADVISED OF THE POSSIBILITY OF SUCH DAMAGES AND (C) THE
                      LEGAL OR EQUITABLE THEORY (CONTRACT, TORT OR OTHERWISE) UPON WHICH THE CLAIM IS BASED.
                    </p>
                    <p className="mt-4">
                      SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF CERTAIN WARRANTIES OR THE LIMITATION OR EXCLUSION
                      OF LIABILITY FOR CERTAIN TYPES OF DAMAGES. THEREFORE, SOME OF THE ABOVE LIMITATIONS IN THIS
                      SECTION MIGHT NOT APPLY TO YOU.
                    </p>
                    <p className="mt-4">
                      NOTHING IN THESE TERMS SHALL AFFECT ANY NON-WAIVABLE STATUTORY RIGHTS THAT APPLY TO YOU.
                    </p>
                    <p className="mt-4">
                      If any provision or provisions of these Terms are held to be invalid, illegal, or unenforceable,
                      the validity, legality and enforceability of the remaining provisions shall remain in full force
                      and effect.
                    </p>
                  </li>
                </ol>
              </li>

              <li className="mt-4">
                <span className="font-semibold">Indemnification</span> To the fullest extent permitted by applicable
                law, you agree to defend, indemnify and hold us and our affiliates, and each of their directors,
                officers, employees, advisors, consultants, agents and representatives (collectively, “Indemnified
                Parties”) harmless from and against any and all claims, damages, losses, costs investigations,
                liabilities, judgments, fines, penalties, settlements, interest and expenses (including attorneys’ fees)
                that directly or indirectly arise from or are related to any claim, suit, action, demand or proceeding
                made or brought by a third party against an Indemnified Party, or on account of the investigation,
                defense or settlement thereof, arising out of or in connection with (a) your use of the Services,
                including the minting and creation of your NFT, (b) your breach or anticipatory breach of these Terms,
                (c) your violation or anticipatory violation of any laws, rules, regulations, codes, statutes,
                ordinances, or orders of any governmental and quasi-governmental authorities in connection with your use
                of the Services or the NFTs, (c) any actual or alleged infringement of the intellectual property rights
                of others by you, (d) your failure to pay any Taxes or Sales Taxes in connection with your transactions
                while using the Services or to provide us with a properly executed tax form described in Section 12
                Taxes, and (e) any act of gross negligence, willful or intentional conduct by you. You agree to promptly
                notify us of any third-party claims and cooperate as fully required by us in the defense of any such
                claims and losses. We retain the exclusive right to assume the exclusive defense and control of any
                claims and losses. You will not settle any claims and losses without our prior written consent. THIS
                INDEMNITY IS IN ADDITION TO, AND NOT IN LIEU OF, ANY OTHER INDEMNITIES SET FORTH IN A WRITTEN AGREEMENT
                BETWEEN YOU AND US.
                <p className="mt-4">
                  The Indemnified Parties reserve the right to seek all remedies available at law or in equity for your
                  violation of these Terms, including the right to block access from a particular Internet address to
                  our Services and report misuses to law enforcement.
                </p>
              </li>
              <li className="mt-4">
                <span className="font-semibold">Dispute Resolution and Arbitration</span>
                <p className="mt-4 font-semibold">PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR RIGHTS.</p>
                <ol className={orderedListRoman}>
                  <li className="mt-4">
                    <span className="font-semibold">Waiver of Rights.</span> YOU AGREE THAT BY ACCESSING OR USING OUR
                    SERVICES, YOU ARE WAIVING THE RIGHT TO A COURT OR JURY TRIAL OR TO PARTICIPATE IN A CLASS ACTION.
                    YOU AGREE THAT YOU MAY BRING CLAIMS AGAINST THE INDEMNIFIED PARTIES ONLY IN YOUR INDIVIDUAL CAPACITY
                    AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS, REPRESENTATIVE OR COLLECTIVE
                    PROCEEDING. ANY ARBITRATION WILL TAKE PLACE ON AN INDIVIDUAL BASIS. CLASS ARBITRATIONS AND CLASS
                    ACTIONS ARE NOT PERMITTED.
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Agreement to Arbitrate.</span>
                    <ol className={orderedListAlpha}>
                      <li className="mt-4">
                        YOU AGREE THAT ANY AND ALL CLAIMS AND DISPUTES ARISING FROM OR RELATING IN ANY WAY TO THESE
                        TERMS, YOUR ACCESS TO OR USE OF THE SERVICES, OR YOUR DEALINGS WITH THE INDEMNIFIED PARTIES IN
                        CONNECTION WITH THE FOREGOING SHALL BE FINALLY SETTLED AND RESOLVED THROUGH BINDING, INDIVIDUAL
                        ARBITRATION AS DESCRIBED IN THIS SECTION.
                      </li>
                      <li className="mt-4">
                        THIS AGREEMENT TO ARBITRATE INVOLVES INTERSTATE COMMERCE, AND, THEREFORE, SHALL BE GOVERNED BY
                        THE FEDERAL ARBITRATION ACT, 9 U.S.C. 1-16 (“FAA”), AND NOT BY STATE LAW.
                      </li>
                      <li className="mt-4">
                        THIS AGREEMENT TO ARBITRATE IS INTENDED TO BE INTERPRETED BROADLY PURSUANT TO THE FAA.
                      </li>
                      <li className="mt-4">
                        THE ARBITRATION WILL BE GOVERNED BY THE COMMERCIAL ARBITRATION RULES AND THE SUPPLEMENTARY
                        PROCEDURES FOR CONSUMER RELATED DISPUTES OF THE AMERICAN ARBITRATION ASSOCIATION (“AAA”), AS
                        MODIFIED BY THIS SECTION.
                      </li>
                      <li className="mt-4">
                        FOR ANY CLAIM WHERE THE TOTAL AMOUNT OF THE AWARD SOUGHT IS $10,000 OR LESS, YOU MUST ABIDE BY
                        THE FOLLOWING RULES: (1) THE ARBITRATION SHALL BE CONDUCTED SOLELY BASED ON TELEPHONE OR ONLINE
                        APPEARANCES AND/OR WRITTEN SUBMISSIONS AND (2) THE ARBITRATION SHALL NOT INVOLVE ANY PERSONAL
                        APPEARANCE BY THE PARTIES OR WITNESSES UNLESS OTHERWISE MUTUALLY AGREED BY THE PARTIES. IF THE
                        CLAIM EXCEEDS $10,000, THE RIGHT TO A HEARING WILL BE DETERMINED BY THE AAA RULES, AND THE
                        HEARING (IF ANY) WILL TAKE PLACE IN UTAH COUNTY, UTAH.
                      </li>
                      <li className="mt-4">
                        THE ARBITRATOR IS BOUND BY THE TERMS OF THIS AGREEMENT TO ARBITRATE. YOU ACKNOWLEDGE AND AGREE
                        THAT, IN ANY ARBITRATION PROCEEDING, NO DEPOSITIONS WILL BE TAKEN, AND ALL OTHER FORMS OF
                        DISCOVERY OF FACTS WILL BE LIMITED TO THOSE THINGS THAT THE ARBITRATOR DETERMINES, IN ITS SOLE
                        DISCRETION, TO BE NECESSARY. FURTHER, IN ANY ARBITRATION PROCEEDING: (1) THERE SHALL BE NO
                        PUNITIVE, EXEMPLARY, INCIDENTAL, OR CONSEQUENTIAL OR OTHER SPECIAL DAMAGES; (2) ALL DAMAGES,
                        CLAIMS, AND AWARDS WILL BE GOVERNED BY UTAH LAW; (3) THE PARTIES WILL CONDUCT THE ARBITRATION
                        CONFIDENTIALLY AND EXPEDITIOUSLY AND WILL PAY THEIR OWN COSTS AND EXPENSES OF ARBITRATION,
                        INCLUDING THEIR OWN ATTORNEY’S FEES. IF YOU PROVE THAT YOU ARE UNABLE TO AFFORD THE AAA FEE, YOU
                        AGREE TO NOTIFY ALL PERSONS AGAINST WHOM YOU HAVE AN ARBITRABLE CLAIM AND GIVE SUCH PERSONS THE
                        OPPORTUNITY, INDIVIDUALLY AND AS A GROUP, TO PAY SUCH FEE. THE PROCEEDING AND THE DECISION SHALL
                        BE KEPT CONFIDENTIAL BY THE PARTIES.
                      </li>
                      <li className="mt-4">
                        THE ARBITRATOR MAY AWARD DECLARATORY OR INJUNCTIVE RELIEF ONLY IN FAVOR OF THE INDIVIDUAL PARTY
                        SEEKING RELIEF AND ONLY TO THE EXTENT NECESSARY TO PROVIDE RELIEF WARRANTED BY THAT PARTY’S
                        INDIVIDUAL CLAIM.
                      </li>
                      <li className="mt-4">
                        THE ARBITRATOR’S RULING IS BINDING AND MAY BE ENTERED AS A JUDGMENT IN ANY COURT OF COMPETENT
                        JURISDICTION, OR APPLICATION MAY BE MADE TO SUCH COURT FOR JUDICIAL ACCEPTANCE OF ANY AWARD AND
                        AN ORDER OF ENFORCEMENT, AS THE CASE MAY BE.
                      </li>
                      <li className="mt-4">
                        EXCEPT IN THE EVENT YOU OPT OUT OF THIS AGREEMENT TO ARBITRATE IN ACCORDANCE WITH SECTION 11.5,
                        THIS AGREEMENT TO ARBITRATE WILL SURVIVE TERMINATION OF THESE TERMS AND/OR YOUR ACCESS TO OR USE
                        OF THE SERVICES AND YOUR RELATIONSHIP WITH THE INDEMNIFIED PARTIES IN CONNECTION THEREWITH.
                      </li>
                      <li className="mt-4">
                        ALL CHALLENGES TO THE VALIDITY AND APPLICABILITY OF THIS AGREEMENT TO ARBITRATE (I.E., WHETHER A
                        PARTICULAR CLAIM OR DISPUTE IS SUBJECT TO ARBITRATION) SHALL BE DETERMINED BY THE ARBITRATOR.
                      </li>
                    </ol>
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Information About Arbitration.</span> INFORMATION ON AAA AND HOW TO
                    START ARBITRATION CAN BE FOUND AT{' '}
                    <ExternalLink className="underline" href="https://www.adr.org">
                      WWW.ADR.ORG
                    </ExternalLink>
                    . THERE IS NO JUDGE OR JURY IN ARBITRATION. ARBITRATION PROCEDURES ARE SIMPLER AND MORE LIMITED THAN
                    RULES APPLICABLE IN COURT AND REVIEW BY A COURT IS LIMITED. YOU WILL NOT BE ABLE TO HAVE A COURT OR
                    JURY TRIAL OR PARTICIPATE IN A CLASS ACTION OR CLASS ARBITRATION. YOU UNDERSTAND AND AGREE THAT BY
                    AGREEING TO RESOLVE ANY DISPUTE THROUGH INDIVIDUAL ARBITRATION, YOU ARE WAIVING THE RIGHT TO A COURT
                    OR JURY TRIAL. ANY DISPUTE SHALL BE ARBITRATED ON AN INDIVIDUAL BASIS, AND NOT AS A CLASS ACTION,
                    REPRESENTATIVE ACTION, CLASS ARBITRATION, OR SIMILAR PROCEEDING. THE ARBITRATOR MAY NOT CONSOLIDATE
                    THE CLAIMS OF MULTIPLE PARTIES.
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Challenges to Validity of Agreement to Arbitrate.</span> INFORMATION
                    ON AAA AND HOW TO START ARBITRATION CAN BE FOUND AT{' '}
                    <ExternalLink className="underline" href="https://www.adr.org">
                      WWW.ADR.ORG
                    </ExternalLink>
                    . THERE IS NO JUDGE OR JURY IN ARBITRATION. ARBITRATION PROCEDURES ARE SIMPLER AND MORE LIMITED THAN
                    RULES APPLICABLE IN COURT AND REVIEW BY A COURT IS LIMITED. YOU WILL NOT BE ABLE TO HAVE A COURT OR
                    JURY TRIAL OR PARTICIPATE IN A CLASS ACTION OR CLASS ARBITRATION. YOU UNDERSTAND AND AGREE THAT BY
                    AGREEING TO RESOLVE ANY DISPUTE THROUGH INDIVIDUAL ARBITRATION, YOU ARE WAIVING THE RIGHT TO A COURT
                    OR JURY TRIAL. ANY DISPUTE SHALL BE ARBITRATED ON AN INDIVIDUAL BASIS, AND NOT AS A CLASS ACTION,
                    REPRESENTATIVE ACTION, CLASS ARBITRATION, OR SIMILAR PROCEEDING. THE ARBITRATOR MAY NOT CONSOLIDATE
                    THE CLAIMS OF MULTIPLE PARTIES.
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Opt-Out of Agreement to Arbitrate.</span> IF YOU WISH TO OPT-OUT OF
                    THE AGREEMENT TO ARBITRATE, WITHIN 45 DAYS OF APRIL 5TH, 2022 OR WHEN YOU FIRST ACCESS OR USE OUR
                    SERVICES, WHICHEVER IS LATER, YOU MUST SEND US A LETTER STATING: “REQUEST TO OPT-OUT OF AGREEMENT TO
                    ARBITRATE” TO:
                    <p className="mt-4">
                      <p>Angel Studios Inc.</p>
                      <p>295 W Center St Suite A</p>
                      <p>Provo, UT 84601</p>
                    </p>
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Venue for Litigation.</span> IF THE AGREEMENT TO ARBITRATE IN
                    SECTION 11.2 ABOVE IS FOUND UNENFORCEABLE OR TO NOT APPLY FOR A GIVEN DISPUTE, OR IF YOU OPT OUT OF
                    THE AGREEMENT TO ARBITRATE IN ACCORDANCE WITH SECTION 11.5, THEN, UNLESS PROHIBITED BY APPLICABLE
                    LAW, THE PROCEEDINGS MUST BE BROUGHT EXCLUSIVELY IN THE STATE AND FEDERAL COURTS COVERING UTAH
                    COUNTY, UTAH, AS APPROPRIATE. YOU ALSO THEREFORE AGREE TO SUBMIT TO THE PERSONAL JURISDICTION OF
                    EACH OF THESE COURTS FOR THE PURPOSES OF LITIGATING SUCH CLAIMS OR DISPUTES, AND YOU HEREBY WAIVE
                    YOUR RIGHT TO A JURY TRIAL, WAIVE YOUR RIGHT TO INITIATE OR PARTICIPATE IN A CLASS OR COLLECTIVE
                    ACTION, AND AGREE TO REMAIN BOUND BY ANY AND ALL LIMITATIONS OF LIABILITY AND DAMAGES INCLUDED IN
                    THIS AGREEMENT.
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Time to Bring Claim.</span> ANY CAUSE OF ACTION OR CLAIM YOU MAY
                    HAVE ARISING OUT OF OR RELATING IN ANY WAY TO THE AGREEMENTS, YOUR ACCESS TO OR USE OF THE PLATFORM,
                    OR YOUR DEALINGS WITH THE RELEASED PARTIES MUST BE COMMENCED IN ARBITRATION OR LITIGATION (AS
                    APPLICABLE) WITHIN TWO (2) YEARS AFTER THE CAUSE OF ACTION ACCRUES. AFTER THAT TWO (2) YEAR PERIOD,
                    SUCH CAUSE OF ACTION OR CLAIMS IS PERMANENTLY BARRED. SOME JURISDICTIONS DO NOT ALLOW TIME
                    LIMITATIONS OTHER THAN THOSE SET FORTH IN SUCH STATE’S STATUTE OF LIMITATIONS. IN SUCH CASES, THE
                    APPLICABLE STATUTE OF LIMITATIONS PROVIDED FOR UNDER THE LAWS OF SUCH STATE SHALL APPLY.
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Prevailing Party.</span> IN ANY ARBITRATION PROCEEDING OR ANY LEGAL
                    SUIT, ACTION, OR PROCEEDING (AS APPLICABLE) BETWEEN YOU AND THE INDEMNIFIED PARTIES RELATED TO THESE
                    TERMS, YOUR ACCESS TO OR USE OF THE SERVICES, OR YOUR DEALINGS WITH THE INDEMNIFIED PARTIES IN
                    CONNECTION THEREWITH, THE PREVAILING PARTY SHALL HAVE THE RIGHT TO COLLECT FROM THE NON-PREVAILING
                    PARTY ITS REASONABLE LEGAL FEES AND COSTS, INCLUDING, WITHOUT LIMITATION, ATTORNEY’S FEES, EXPENSES,
                    AND COURT COSTS INCURRED IN CONNECTION THEREWITH.
                  </li>
                </ol>
              </li>

              <li className="mt-4">
                <span className="font-semibold">Taxes</span> We are not responsible for determining the withholding,
                sales, use, value added, transfer or other taxes, together with any interest and penalties imposed with
                respect thereto that may apply to transactions related to the Services (collectively, “Taxes”). You
                agree that you are solely responsible for determining what, if any, Taxes apply to your transactions and
                to withhold, collect, report, and remit the correct amounts of Taxes to the appropriate taxing
                authorities. Unless otherwise indicated on an applicable invoice, amounts due are exclusive of sale,
                use, value added or similar Taxes (“Sales Taxes”). This means that Sales Taxes become your sole
                responsibility. Upon our request, you agree to promptly provide a properly executed Internal Revenue
                Service Form W-9 or applicable Internal Revenue Service W-8 and any other tax form that is reasonably
                required by us to comply with our tax reporting obligations.
              </li>

              <li className="mt-4">
                <span className="font-semibold">Termination</span>
                <ol className={orderedListRoman}>
                  <li className="mt-4">
                    <span className="font-semibold">Right to Terminate.</span> Subject to the terms set forth in the
                    applicable Agreements, in addition to, and not in lieu of, any of our other rights set forth in
                    these Terms, we reserve the right, with or without notice and in our sole discretion, to terminate
                    these Terms, your account, and/or your ability to access or use our Platform for any reason,
                    including, without limitation, for lack of use or if we believe that you have violated or acted
                    inconsistently with the letter or spirit of these Terms, or in the case of any activity by you that
                    may harm us or other users, including, but not limited to, fraud, abuse of privileges, or misuse of
                    our Platform. You agree that we will not be liable to you or any third party for any such
                    termination.
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Effects of Termination.</span> If we exercise our termination rights
                    available under the applicable Agreements, your license to access and use our Platform shall
                    immediately terminate and you must discontinue all access to and use of our Platform affected by
                    such termination.
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Fraudulent Activity.</span> If we suspect that you are engaging in
                    any fraudulent, abusive, or illegal activity, we may refer such matter to appropriate law
                    enforcement authorities.
                  </li>
                  <li className="mt-4">
                    <span className="font-semibold">Survival.</span> The provisions of these Terms which by their nature
                    are intended to survive the termination or cancellation of these Terms shall continue as valid and
                    enforceable obligations notwithstanding any such termination or cancellation. Without limiting the
                    foregoing, the provisions of these Terms regarding indemnity and limitations of liability shall
                    survive the termination or cancellation of these Terms.
                  </li>
                </ol>
              </li>

              <li className="mt-4">
                <span className="font-semibold">Governing Law</span> These Terms shall be governed by and construed in
                accordance with the laws of the United States of America and the State of Utah without regard to
                conflicts-of-law provisions. These Terms do not limit any consumer protection rights that you may be
                entitled to under the mandatory laws of your state of residence.
              </li>

              <li className="mt-4">
                <span className="font-semibold">Comments and Feedback</span> Angel Studios is free to use any comments,
                information, ideas, concepts, reviews, techniques or other materials contained in any communication you
                send to us (“Feedback”), including responses to questionnaires or through postings to our Sites,
                including the AMP and bulletin board and user interfaces, worldwide and in perpetuity without further
                compensation, acknowledgement or payment to you for any purpose whatsoever including, but not limited
                to, developing, manufacturing and marketing products and creating, modifying or improving Angel Studios’
                NFTs. In addition, you agree not to enforce any “moral rights” in and to the Feedback, to the extent
                permitted by applicable law. Please note that Angel Studios does not accept unsolicited materials or
                ideas for Angel Studios content except pursuant to the submission of a completed Angel Studios, Inc.
                Submission Release Agreement and is not responsible for the similarity of any of its content or
                programming in any media to materials or ideas transmitted to Angel Studios. Should you send any
                unsolicited materials or ideas to us other than pursuant to the Angel Studios, Inc. Submission Release
                Agreement, you do so with the understanding that no additional consideration of any sort will be
                provided to you, and you are waiving any claim against Angel Studios and its affiliates regarding the
                use of any such material or idea, even if any material or idea we may later use is substantially or
                strikingly similar to the material or idea you sent.
              </li>

              <li className="mt-4">
                <span className="font-semibold">Links to Third Party Websites</span> Our Services may include links to
                other websites or services solely as a convenience to you. You acknowledge and agree that we are not
                responsible for the availability of such external websites or services and that we do not endorse and
                are not responsible or liable for any such linked websites or services or any information, material,
                products, or services contained on or accessible through other websites or services. Furthermore, we
                make no express or implied warranties with regard to the information, material, products, or services
                that are contained on or accessible through linked websites or services. Your access to and use of
                linked websites or services, including, without limitation, any information, material, products, and
                services on or accessible through other websites or services is solely at your own risk.
              </li>

              <li className="mt-4">
                <span className="font-semibold">Customer Support</span> For more information about our Services or AMP,
                to query about an NFT, or to request assistance with a purchase, please contact us at
                <a className="underline" href="mailto:support@angel.com">
                  support@angel.com
                </a>
                . Most users’ concerns can be resolved quickly and to a user’s satisfaction by sending an e-mail message
                to Angel Studios’ Customer Service Department at{' '}
                <a className="underline" href="mailto:support@angel.com">
                  support@angel.com
                </a>
                . You may also write to Angel Studios at the following address:
                <p className="mt-4">
                  <p>Angel Studios Inc.</p>
                  <p>Attn: Angel Market Terms of Service</p>
                  <p>295 W Center St Suite A</p>
                  <p>Provo, UT 84601</p>
                </p>
                <p className="mt-4">
                  In the event of conflict between these Terms and information provided by Customer Support or other
                  portions of our website, these Terms will control.
                </p>
              </li>

              <li className="mt-4">
                <span className="font-semibold">Waiver</span> Angel’s failure to exercise or enforce any right or
                provision of the Terms shall not constitute a waiver of such right or provision. No waiver shall be
                effective against Angel Studios unless made in writing, and no such waiver shall be construed as a
                waiver in any other or subsequent instance.
              </li>

              <li className="mt-4">
                <span className="font-semibold">Severability</span> If any term, clause, or provision of these Terms is
                held invalid or unenforceable, then that term, clause, or provision shall be severable from these Terms
                and will not affect the validity or enforceability of any remaining part of that term, cause, or
                provision, or any other terms, clause, or provision of these Terms.
              </li>

              <li className="mt-4">
                <span className="font-semibold">Headings</span> The section headings and sub-headings contained in the
                Terms are for convenience only and have no legal or contractual effect.
              </li>

              <li className="mt-4">
                <span className="font-semibold">Electronic Communications</span> The communications between you and
                Angel Studios use electronic means, whether you access or use our Services or send us emails, or whether
                we post notices on our Services or communicate with you via email. For contractual purposes, you: (1)
                consent to receive communications from us in an electronic form; and (2) agree that all terms and
                conditions, agreements, notices, disclosures, and other communications that we provide to you
                electronically satisfy any legal requirement that such communications would satisfy if they were
                provided in a hardcopy writing. The foregoing does not affect your non-waivable rights.
              </li>

              <li className="mt-4">
                <span className="font-semibold">Admissibility of Terms</span> A printed version of these Terms and of
                any notice given in electronic form shall be admissible in arbitration, judicial, or administrative
                proceedings based upon or relating to these Terms to the same extent and subject to the same conditions
                as other business documents and records originally generated and maintained in printed form.
              </li>

              <li className="mt-4">
                <span className="font-semibold">Assignment</span> We reserve the right to transfer, assign, sublicense,
                or pledge these Terms, in whole or in part, to any person without notice, provided that any such
                assignment will be on the same terms or terms that are no less advantageous to you. You may not assign,
                sublicense, or otherwise transfer in any manner whatsoever any of your rights or obligations under these
                Terms.
              </li>

              <li className="mt-4">
                <span className="font-semibold">Entire Agreement</span> Except as expressly agreed by you and us in
                writing, these Terms constitute the entire agreement between you and us with respect to the Services and
                supersede all prior communications, promises and proposals, whether oral, written, or electronic,
                between you and us with respect to the Services.
              </li>
            </ol>
          </div>
        </PaddedContainer>
      </LazyHydrate>
    </div>
  )
}

MarketplaceTermsOfServicePage.getLayout = (page) => {
  return <LegalLayout>{page}</LegalLayout>
}

export const getStaticProps = getStaticPropsErrorHandler(
  { filePath: 'pages/[locale]/legal/marketplace-terms-of-service.tsx' },
  async ({ locale }) => {
    return {
      props: {
        ...(await loadTranslations(locale, ['common', 'account'])),
      },
      revalidate: false,
    }
  },
)

export const getStaticPaths = getGenericStaticPaths

export default MarketplaceTermsOfServicePage
